import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRbac } from "@/hooks/useRbac";
import { useModalities } from "@/contexts/ModalitiesContext";
import { getModalityChartColor } from "@/lib/modalities";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import {
  ANATOMY_TYPE_1_OPTIONS,
  getAnatomyType2Options,
  getAnatomyType3Options,
  needsAnatomyType2,
  needsAnatomyType3,
  normalizeAnatomyType2,
} from "@/lib/anatomyTypes";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Share2, ChevronLeft, ChevronRight, Pencil, ChevronsUpDown, X, Trash2, Download, Loader2, Filter, Calendar } from "lucide-react";
import SharePatientDialog from "@/components/SharePatientDialog";
import EditPatientDialog from "@/components/EditPatientDialog";
import ModalityDotLabel from "@/components/ModalityDotLabel";
import { PageHeaderActionsRight, PageHeaderTitleSuffix } from "@/contexts/PageHeaderContext";
import { formatApiErrorDetail, parseAxiosErrorDetail } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseStudyTypesParam = (raw) => {
  if (!raw) return [];
  return [...new Set(String(raw).split(",").map((t) => t.trim()).filter(Boolean))];
};

const parseStudyTypeCounts = (raw) => {
  const counts = {};
  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      if (!item || typeof item !== "object") return;
      Object.entries(item).forEach(([key, value]) => {
        const type = String(key ?? "").trim();
        if (!type) return;
        const n = Number(value);
        counts[type] = Number.isFinite(n) ? n : 0;
      });
    });
    return counts;
  }
  if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const type = String(key ?? "").trim();
      if (!type) return;
      const n = Number(value);
      counts[type] = Number.isFinite(n) ? n : 0;
    });
  }
  return counts;
};

const parseAnonymizedParam = (raw) => {
  if (raw === "true" || raw === "false") return raw;
  return "";
};

const parseStatusParam = (raw) => {
  if (raw === "pending" || raw === "completed") return raw;
  return "";
};

const parseGenderParam = (raw) => {
  const g = String(raw ?? "").trim();
  if (g === "M" || g === "Male") return "M";
  if (g === "F" || g === "Female") return "F";
  if (g === "Other") return "Other";
  return "";
};

const parseDateParam = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const day = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "";
};

const toCreatedDateFrom = (date) => (date ? `${date}T00:00:00Z` : undefined);
const toCreatedDateTo = (date) => (date ? `${date}T23:59:59Z` : undefined);

const toNameList = (items) => {
  if (!Array.isArray(items)) return [];
  return [...new Set(
    items
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean)
      .map(String),
  )];
};

const withCurrentOption = (list, current) => {
  if (!current) return list;
  if (list.includes(current)) return list;
  return [current, ...list];
};

const COMPANY_OPTIONS = [
  "Medtronic",
  "Abbott",
  "Edwards",
  "Meril",
  "SMT",
  "Microport",
  "Allegra",
  "Boston Scientific",
  "Corcym",
  "Others",
];

const FREE_TEXT_SIZE_TYPES = ["Boston Scientific", "Corcym", "Others", "NA/Others"];

const MEDTRONIC_VALVE_TYPES = ["Evolut Pro+", "Evolut Fx", "Evolut Fx+"];

const MEDTRONIC_VALVE_SIZES = ["23", "26", "29", "34"];

const VALVE_SIZES_BY_TYPE = {
  Meril: ["20", "21.5", "23", "24.5", "26", "27.5", "29", "31.5", "34"],
  Edwards: ["20", "23", "26", "29"],
  Abbott: ["23", "25", "27", "29", "35"],
  SMT: ["22", "26", "30"],
  Microport: ["21", "24", "27", "30"],
  Allegra: ["21", "23", "27"],
};

const getSelectedCompany = (category, valveType) => {
  if (category === "Medtronic") return "Medtronic";
  if (category !== "Competition") return "";
  if (valveType === "NA/Others") return "Others";
  return valveType || "";
};

const formFromCompany = (company) => {
  if (company === "Medtronic") {
    return { valve_manufacturer_category: "Medtronic", valve_type: "", valve_size: "" };
  }
  return { valve_manufacturer_category: "Competition", valve_type: company, valve_size: "" };
};

const getValveSizeOptions = (valveType) => {
  if (MEDTRONIC_VALVE_TYPES.includes(valveType)) return MEDTRONIC_VALVE_SIZES;
  if (FREE_TEXT_SIZE_TYPES.includes(valveType)) return null;
  return VALVE_SIZES_BY_TYPE[valveType] ?? [];
};

const buildPatientFilterParams = ({
  search,
  anonymized,
  status,
  studyTypes,
  organization,
  organizationId,
  doctor,
  institutionName,
  ageMin,
  ageMax,
  gender,
  userId,
  anatomyType1,
  anatomyType2,
  anatomyType3,
  createdDateFrom,
  createdDateTo,
  tag,
  valveManufacturer,
}) => ({
  search: search || undefined,
  anonymized: anonymized || undefined,
  status: status || undefined,
  study_types: studyTypes.length ? studyTypes.join(",") : undefined,
  organization: organization || undefined,
  organization_id: organizationId || undefined,
  doctor: doctor || undefined,
  institution_name: institutionName || undefined,
  age_min: ageMin || undefined,
  age_max: ageMax || undefined,
  gender: gender || undefined,
  user_id: userId || undefined,
  anatomy_type_1: anatomyType1 || undefined,
  anatomy_type_2: anatomyType2 || undefined,
  anatomy_type_3: anatomyType3 || undefined,
  created_date_from: toCreatedDateFrom(createdDateFrom),
  created_date_to: toCreatedDateTo(createdDateTo),
  tag: tag || undefined,
  valve_manufacturer: valveManufacturer || undefined,
});

const filenameFromDisposition = (disposition, fallback = "patients.csv") => {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const HOSPITAL_NAME_MAX_LENGTH = 16;

const truncateHospitalName = (name) => {
  if (!name || name.length <= HOSPITAL_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, HOSPITAL_NAME_MAX_LENGTH)}…`;
};

export default function Patients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canManagePatients, canShare, canExportPatientCsv } = useRbac();
  const { modalities, getModality } = useModalities();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const anonymized = useMemo(
    () => parseAnonymizedParam(searchParams.get("anonymized")),
    [searchParams],
  );
  const status = useMemo(
    () => parseStatusParam(searchParams.get("status")),
    [searchParams],
  );
  const studyTypes = useMemo(
    () => parseStudyTypesParam(searchParams.get("study_types")),
    [searchParams],
  );
  const organization = searchParams.get("organization")?.trim() || "";
  const organizationId = searchParams.get("organization_id")?.trim() || "";
  const doctor = searchParams.get("doctor")?.trim() || "";
  const institutionName = searchParams.get("institution_name")?.trim() || organization;
  const ageMin = searchParams.get("age_min")?.trim() || "";
  const ageMax = searchParams.get("age_max")?.trim() || "";
  const gender = useMemo(
    () => parseGenderParam(searchParams.get("gender")),
    [searchParams],
  );
  const userId = searchParams.get("user_id")?.trim() || "";
  const anatomyType1 = searchParams.get("anatomy_type_1")?.trim() || "";
  const anatomyType2 = searchParams.get("anatomy_type_2")?.trim() || "";
  const anatomyType3 = searchParams.get("anatomy_type_3")?.trim() || "";
  const createdDateFrom = useMemo(
    () => parseDateParam(searchParams.get("created_date_from")),
    [searchParams],
  );
  const createdDateTo = useMemo(
    () => parseDateParam(searchParams.get("created_date_to")),
    [searchParams],
  );
  const tag = searchParams.get("tag")?.trim() || "";
  const valveManufacturer = searchParams.get("valve_manufacturer")?.trim() || "";
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(13);
  const [loading, setLoading] = useState(true);
  const [sharePatient, setSharePatient] = useState(null);
  const [editPatient, setEditPatient] = useState(null);
  const [updatingStatusUhid, setUpdatingStatusUhid] = useState(null);
  const [completeDialogPatient, setCompleteDialogPatient] = useState(null);
  const [deletePatient, setDeletePatient] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [tags, setTags] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [completionForm, setCompletionForm] = useState({
    valve_manufacturer_category: "",
    valve_type: "",
    valve_size: "",
  });

  const currentFilterValues = {
    anonymized,
    status,
    studyTypes,
    organization,
    organizationId,
    doctor,
    institutionName,
    ageMin,
    ageMax,
    gender,
    userId,
    anatomyType1,
    anatomyType2,
    anatomyType3,
    createdDateFrom,
    createdDateTo,
    tag,
    valveManufacturer,
  };

  const hasActiveFilters = Boolean(anonymized)
    || Boolean(status)
    || studyTypes.length > 0
    || Boolean(organization)
    || Boolean(organizationId)
    || Boolean(doctor)
    || Boolean(institutionName)
    || Boolean(ageMin)
    || Boolean(ageMax)
    || Boolean(gender)
    || Boolean(userId)
    || Boolean(anatomyType1)
    || Boolean(anatomyType2)
    || Boolean(anatomyType3)
    || Boolean(createdDateFrom)
    || Boolean(createdDateTo)
    || Boolean(tag)
    || Boolean(valveManufacturer);

  const syncFilterParams = (next = {}) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const merged = { ...currentFilterValues, ...next };

      const setOrDelete = (key, value) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };

      setOrDelete("anonymized", merged.anonymized);
      setOrDelete("status", merged.status);

      if (merged.studyTypes?.length) params.set("study_types", merged.studyTypes.join(","));
      else params.delete("study_types");

      setOrDelete("organization_id", merged.organizationId);
      setOrDelete("doctor", merged.doctor);
      setOrDelete("institution_name", merged.institutionName);
      if (merged.institutionName || !merged.organization) params.delete("organization");
      else setOrDelete("organization", merged.organization);
      setOrDelete("age_min", merged.ageMin);
      setOrDelete("age_max", merged.ageMax);
      setOrDelete("gender", merged.gender);
      setOrDelete("user_id", merged.userId);
      setOrDelete("anatomy_type_1", merged.anatomyType1);
      setOrDelete("anatomy_type_2", merged.anatomyType2);
      setOrDelete("anatomy_type_3", merged.anatomyType3);
      setOrDelete("created_date_from", merged.createdDateFrom);
      setOrDelete("created_date_to", merged.createdDateTo);
      setOrDelete("tag", merged.tag);
      setOrDelete("valve_manufacturer", merged.valveManufacturer);

      return params;
    }, { replace: true });
    setPage(1);
  };

  const syncStudyTypes = (types) => {
    syncFilterParams({ studyTypes: types });
  };

  const fetchPatients = async (q = search, currentPage = page, filters = currentFilterValues) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients`, {
        params: {
          ...buildPatientFilterParams({
            search: q,
            ...filters,
          }),
          page: currentPage,
          limit,
        },
      });
      setPatients(res.data?.patients ?? []);
      setTotal(res.data?.total ?? 0);
      const apiPage = res.data?.page;
      if (typeof apiPage === "number" && apiPage > 0) {
        setPage((prev) => (prev === apiPage ? prev : apiPage));
      }
    } catch (e) {
      console.error(e);
      setPatients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      setFilterOptionsLoading(true);
      try {
        const [doctorsRes, institutionsRes, tagsRes, orgsRes, usersRes] = await Promise.allSettled([
          axios.get(`${API}/patients/ref/doctors`),
          axios.get(`${API}/patients/ref/institutions`),
          axios.get(`${API}/patients/ref/tags`),
          axios.get(`${API}/organizations`, { params: { page: 1, limit: 200 } }),
          axios.get(`${API}/users`, { params: { page: 1, limit: 200 } }),
        ]);

        if (doctorsRes.status === "fulfilled") {
          setDoctors(toNameList(doctorsRes.value.data?.doctors ?? doctorsRes.value.data));
        } else {
          setDoctors([]);
        }

        if (institutionsRes.status === "fulfilled") {
          setInstitutions(toNameList(
            institutionsRes.value.data?.institutions ?? institutionsRes.value.data,
          ));
        } else {
          setInstitutions([]);
        }

        if (tagsRes.status === "fulfilled") {
          const tagList = Array.isArray(tagsRes.value.data?.tags)
            ? tagsRes.value.data.tags
            : Array.isArray(tagsRes.value.data)
              ? tagsRes.value.data
              : [];
          setTags(tagList.filter(Boolean).map(String));
        } else {
          setTags([]);
        }

        if (orgsRes.status === "fulfilled") {
          const items = orgsRes.value.data?.organizations ?? orgsRes.value.data?.items ?? [];
          setOrganizations(Array.isArray(items) ? items.filter((org) => org?.id) : []);
        } else {
          setOrganizations([]);
        }

        if (usersRes.status === "fulfilled") {
          const items = usersRes.value.data?.users ?? usersRes.value.data?.items ?? [];
          setUsers(Array.isArray(items) ? items.filter((u) => u?.id) : []);
        } else {
          setUsers([]);
        }
      } finally {
        setFilterOptionsLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    fetchPatients(search, page, currentFilterValues);
  }, [
    search, page, anonymized, studyTypes, status, organization, organizationId, doctor,
    institutionName, ageMin, ageMax, gender, userId, anatomyType1, anatomyType2, anatomyType3,
    createdDateFrom, createdDateTo, tag, valveManufacturer,
  ]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/patients/export`, {
        params: buildPatientFilterParams({
          search,
          ...currentFilterValues,
        }),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(res.headers?.["content-disposition"]);
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      const detail = await parseAxiosErrorDetail(e);
      toast.error(formatApiErrorDetail(detail) || "Failed to export patients");
    } finally {
      setExporting(false);
    }
  };

  const handleStatusFilterChange = (value) => {
    syncFilterParams({ status: value === "all" ? "" : value });
  };

  const handlePrivacyFilterChange = (value) => {
    syncFilterParams({ anonymized: value === "all" ? "" : value });
  };

  const handleStudyTypeToggle = (key) => {
    syncStudyTypes(
      studyTypes.includes(key)
        ? studyTypes.filter((t) => t !== key)
        : [...studyTypes, key],
    );
  };

  const handleAnatomyType1Change = (value) => {
    const nextType1 = value === "all" ? "" : value;
    syncFilterParams({
      anatomyType1: nextType1,
      anatomyType2: "",
      anatomyType3: "",
    });
  };

  const handleAnatomyType2Change = (value) => {
    const nextType2 = value === "all" ? "" : value;
    syncFilterParams({
      anatomyType2: nextType2,
      anatomyType3: "",
    });
  };

  const clearFilters = () => {
    syncFilterParams({
      anonymized: "",
      status: "",
      studyTypes: [],
      organization: "",
      organizationId: "",
      doctor: "",
      institutionName: "",
      ageMin: "",
      ageMax: "",
      gender: "",
      userId: "",
      anatomyType1: "",
      anatomyType2: "",
      anatomyType3: "",
      createdDateFrom: "",
      createdDateTo: "",
      tag: "",
      valveManufacturer: "",
    });
  };

  const handleStatusChange = async (uhid, status, completionDetails = null) => {
    setUpdatingStatusUhid(uhid);
    try {
      const payload = {
        status,
        ...(completionDetails ?? {}),
      };
      await axios.put(`${API}/patients/${encodeURIComponent(uhid)}/status`, payload);
      setPatients((prev) => prev.map((p) => (
        p.uhid === uhid
          ? { ...p, status, ...(completionDetails ?? {}) }
          : p
      )));
      toast.success("Patient status updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update status");
    } finally {
      setUpdatingStatusUhid(null);
    }
  };

  const showActions = canShare || canManagePatients;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;
  const selectedCompany = getSelectedCompany(
    completionForm.valve_manufacturer_category,
    completionForm.valve_type,
  );
  const isMedtronicCompany = completionForm.valve_manufacturer_category === "Medtronic";
  const sizeOptions = getValveSizeOptions(completionForm.valve_type);
  const isNaOthers = FREE_TEXT_SIZE_TYPES.includes(completionForm.valve_type);
  const anatomyType2Options = getAnatomyType2Options(anatomyType1);
  const anatomyType3Options = getAnatomyType3Options(anatomyType2);
  const showAnatomyType2 = needsAnatomyType2(anatomyType1);
  const showAnatomyType3 = needsAnatomyType3(anatomyType1) && Boolean(anatomyType2);
  const doctorOptions = withCurrentOption(doctors, doctor);
  const institutionOptions = withCurrentOption(institutions, institutionName);
  const tagOptions = withCurrentOption(tags, tag);
  const selectedUser = users.find((u) => u.id === userId);

  const openMarkCompleteDialog = (patient) => {
    setCompleteDialogPatient(patient);
    setCompletionForm({ valve_manufacturer_category: "", valve_type: "", valve_size: "" });
  };

  const handleConfirmMarkComplete = async () => {
    if (!completeDialogPatient?.uhid) return;
    const { valve_manufacturer_category, valve_type, valve_size } = completionForm;
    if (!valve_manufacturer_category || !valve_type || !valve_size?.trim()) {
      toast.error("Select company, valve type, and size");
      return;
    }
    await handleStatusChange(completeDialogPatient.uhid, "completed", {
      valve_manufacturer_category,
      valve_type,
      valve_size: valve_size.trim(),
    });
    setCompleteDialogPatient(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletePatient?.uhid) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/patients/${encodeURIComponent(deletePatient.uhid)}`);
      toast.success("Patient deleted");
      setDeletePatient(null);
      await fetchPatients(search, page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete patient");
    } finally {
      setDeleting(false);
    }
  };

  const getPatientStatusValue = (status) => (status === "completed" ? "completed" : "pending");

  const patientInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const avatarTone = (uhid) => {
    const code = String(uhid ?? "").split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return code % 2 === 0 ? "bg-[var(--xl-black)]" : "bg-[var(--xl-mut)]";
  };

  return (
    <TooltipProvider delayDuration={150}>
    <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen} className="flex flex-col flex-1 min-h-0">
    <div className="flex flex-col flex-1 min-h-0" data-testid="patients-page">
      <PageHeaderTitleSuffix>
        {!loading ? `(${total})` : null}
      </PageHeaderTitleSuffix>

      <PageHeaderActionsRight>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          <div className="relative min-w-0 w-[min(100%,16rem)] sm:w-56 lg:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--xl-mut2)] pointer-events-none" />
            <Input
              data-testid="patient-search"
              placeholder="Search UHID, name, doctor..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-10 pl-10 text-sm border-[var(--xl-line2)] bg-white"
            />
          </div>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="outline"
              data-testid="patients-filters-toggle"
              aria-expanded={filtersOpen}
              className="h-10 gap-1.5 border-[var(--xl-line2)] bg-white font-normal"
            >
              <Filter className="w-3.5 h-3.5" />
              Filters
              {hasActiveFilters ? (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                  Active
                </Badge>
              ) : null}
            </Button>
          </CollapsibleTrigger>
          {canExportPatientCsv ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={exporting || loading}
              data-testid="patients-export-btn"
              className="h-10 gap-1.5 border-[var(--xl-line2)] bg-white font-normal"
              aria-label="Export patients CSV"
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          ) : null}
        </div>
      </PageHeaderActionsRight>

      <CollapsibleContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
        <Card className="p-4 mb-3 rounded-2xl border border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          {hasActiveFilters ? (
            <div className="flex items-center justify-end gap-2 mb-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="clear-patient-filters"
                className="text-xs gap-1 shrink-0 h-8"
              >
                <X className="w-3.5 h-3.5" /> Clear all
              </Button>
            </div>
          ) : null}
          <div className="flex flex-wrap items-end gap-2 sm:gap-3" data-testid="patient-filters">
            <Select value={status || "all"} onValueChange={handleStatusFilterChange}>
              <SelectTrigger data-testid="filter-status" className="w-full sm:w-36 h-9" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="pending">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={anonymized || "all"} onValueChange={handlePrivacyFilterChange}>
              <SelectTrigger data-testid="filter-privacy" className="w-full sm:w-36 h-9" aria-label="Filter by privacy">
                <SelectValue placeholder="Privacy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All privacy</SelectItem>
                <SelectItem value="true">Anonymized</SelectItem>
                <SelectItem value="false">Full</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={gender || "all"}
              onValueChange={(v) => syncFilterParams({ gender: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-gender" className="w-full sm:w-32 h-9" aria-label="Filter by gender">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All genders</SelectItem>
                <SelectItem value="M">Male</SelectItem>
                <SelectItem value="F">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={valveManufacturer || "all"}
              onValueChange={(v) => syncFilterParams({ valveManufacturer: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-valve-manufacturer" className="w-full sm:w-40 h-9" aria-label="Filter by valve manufacturer">
                <SelectValue placeholder="Valve" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All valves</SelectItem>
                <SelectItem value="medtronic">Medtronic</SelectItem>
                <SelectItem value="competition">Competition</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="filter-study-types"
                  className="h-9 w-full sm:w-auto gap-1.5 font-normal"
                  aria-label="Filter by study types"
                >
                  <span className="truncate max-w-[7rem] sm:max-w-[9rem]">
                    {studyTypes.length
                      ? `${studyTypes.length} type${studyTypes.length === 1 ? "" : "s"}`
                      : "Study types"}
                  </span>
                  <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
                {(modalities ?? []).map((m) => (
                  <DropdownMenuCheckboxItem
                    key={m.key}
                    checked={studyTypes.includes(m.key)}
                    onCheckedChange={() => handleStudyTypeToggle(m.key)}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`filter-study-type-${m.key}`}
                  >
                    <ModalityDotLabel modalityKey={m.key} label={m.label ?? m.key} />
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Select
              value={doctor || "all"}
              onValueChange={(v) => syncFilterParams({ doctor: v === "all" ? "" : v })}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-doctor" className="w-full sm:w-44 h-9" aria-label="Filter by referring doctor">
                <SelectValue placeholder={filterOptionsLoading ? "Loading doctors…" : "Doctor"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All doctors</SelectItem>
                {doctorOptions.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={institutionName || "all"}
              onValueChange={(v) => syncFilterParams({ institutionName: v === "all" ? "" : v, organization: "" })}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-institution" className="w-full sm:w-48 h-9" aria-label="Filter by hospital">
                <SelectValue placeholder={filterOptionsLoading ? "Loading hospitals…" : "Hospital"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All hospitals</SelectItem>
                {institutionOptions.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={organizationId || "all"}
              onValueChange={(v) => syncFilterParams({ organizationId: v === "all" ? "" : v })}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-organization" className="w-full sm:w-48 h-9" aria-label="Filter by organization">
                <SelectValue placeholder={filterOptionsLoading ? "Loading organizations…" : "Organization"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All organizations</SelectItem>
                {organizationId && !organizations.some((org) => String(org.id) === organizationId) ? (
                  <SelectItem value={organizationId}>{organizationId}</SelectItem>
                ) : null}
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={String(org.id)}>
                    {org.name ?? org.organization_name ?? org.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={userId || "all"}
              onValueChange={(v) => syncFilterParams({ userId: v === "all" ? "" : v })}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-user" className="w-full sm:w-44 h-9" aria-label="Filter by user">
                <SelectValue placeholder={filterOptionsLoading ? "Loading users…" : "User"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All users</SelectItem>
                {userId && !selectedUser ? (
                  <SelectItem value={userId}>{userId}</SelectItem>
                ) : null}
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email || u.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={tag || "all"}
              onValueChange={(v) => syncFilterParams({ tag: v === "all" ? "" : v })}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-tag" className="w-full sm:w-36 h-9" aria-label="Filter by tag">
                <SelectValue placeholder={filterOptionsLoading ? "Loading tags…" : "Tag"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All tags</SelectItem>
                {tagOptions.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={anatomyType1 || "all"}
              onValueChange={handleAnatomyType1Change}
            >
              <SelectTrigger data-testid="filter-anatomy-type-1" className="w-full sm:w-40 h-9" aria-label="Filter by anatomy type">
                <SelectValue placeholder="Anatomy type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All anatomy</SelectItem>
                {ANATOMY_TYPE_1_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showAnatomyType2 ? (
              <Select
                value={anatomyType2 || "all"}
                onValueChange={handleAnatomyType2Change}
              >
                <SelectTrigger data-testid="filter-anatomy-type-2" className="w-full sm:w-52 h-9" aria-label="Filter by anatomy type 2">
                  <SelectValue placeholder="Anatomy type 2" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All type 2</SelectItem>
                  {anatomyType2Options.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {showAnatomyType3 ? (
              <Select
                value={anatomyType3 || "all"}
                onValueChange={(v) => syncFilterParams({ anatomyType3: v === "all" ? "" : v })}
              >
                <SelectTrigger data-testid="filter-anatomy-type-3" className="w-full sm:w-44 h-9" aria-label="Filter by anatomy type 3">
                  <SelectValue placeholder="Anatomy type 3" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All type 3</SelectItem>
                  {anatomyType3Options.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <div className="flex flex-col gap-1 shrink-0 w-full sm:w-auto">
              <Label className="text-[10px] text-muted-foreground">Age</Label>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="Min"
                  value={ageMin}
                  onChange={(e) => syncFilterParams({ ageMin: e.target.value })}
                  className="h-9 w-[4.5rem] sm:w-20 text-xs"
                  data-testid="filter-age-min"
                  aria-label="Minimum age"
                />
                <span className="text-xs text-muted-foreground">–</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="Max"
                  value={ageMax}
                  onChange={(e) => syncFilterParams({ ageMax: e.target.value })}
                  className="h-9 w-[4.5rem] sm:w-20 text-xs"
                  data-testid="filter-age-max"
                  aria-label="Maximum age"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
              <Input
                data-testid="filter-created-from"
                type="date"
                value={createdDateFrom}
                onChange={(e) => syncFilterParams({ createdDateFrom: e.target.value })}
                className="h-9 w-full sm:w-36 text-xs"
                aria-label="Created from date"
              />
              <span className="text-xs text-muted-foreground shrink-0">to</span>
              <Input
                data-testid="filter-created-to"
                type="date"
                value={createdDateTo}
                onChange={(e) => syncFilterParams({ createdDateTo: e.target.value })}
                className="h-9 w-full sm:w-36 text-xs"
                aria-label="Created to date"
              />
            </div>
          </div>
        </Card>
      </CollapsibleContent>

      {/* White card wraps table only */}
      <Card className="overflow-hidden flex-1 min-h-0 flex flex-col rounded-2xl border border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="overflow-auto flex-1 min-h-0">
          <Table className="sticky-table min-w-[1000px]">
            <TableHeader>
              <TableRow className="hover:bg-[var(--xl-black)] border-0">
                <TableHead className="min-w-[240px]">Patient</TableHead>
                <TableHead className="w-28">Age · Sex</TableHead>
                <TableHead>Doctor</TableHead>
                <TableHead>Hospital</TableHead>
                <TableHead className="min-w-[10rem]">Anatomy Type</TableHead>
                {showActions && <TableHead className="w-60 text-center">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody className="text-sm font-normal text-[var(--xl-text)]">
              {loading ? (
                <TableRow><TableCell colSpan={showActions ? 6 : 5} className="text-center py-8 text-muted-foreground font-normal">Loading...</TableCell></TableRow>
              ) : patients.length === 0 ? (
                <TableRow><TableCell colSpan={showActions ? 6 : 5} className="text-center py-8 text-muted-foreground font-normal">No patients found</TableCell></TableRow>
              ) : (
                patients.map((p) => {
                  const hospitalName = p.institution_name?.trim() || p.organization;
                  const hospitalDisplay = hospitalName ? truncateHospitalName(hospitalName) : "-";
                  const anatomyDetails = p.anatomyDetails ?? p.anatomy_details;
                  const anatomyType1Label = String(anatomyDetails?.anatomy_type_1 ?? "").trim();
                  const anatomyType2Label = String(
                    normalizeAnatomyType2(anatomyDetails?.anatomy_type_2) || anatomyDetails?.anatomy_type_2 || "",
                  ).trim();
                  const anatomyType3Label = String(anatomyDetails?.anatomy_type_3 ?? "").trim();
                  const pendingDeletion =
                    p.status === "pending_deletion" || Boolean(p.pending_deletion_at);

                  const colSpan = showActions ? 6 : 5;
                  const studyTypeCounts = parseStudyTypeCounts(p.study_type_counts);
                  const studyTypesPresent = new Set([
                    ...(Array.isArray(p.study_types) ? p.study_types : []),
                    ...Object.keys(studyTypeCounts),
                  ]);
                  const studyTypesList = [
                    ...modalities
                      .map((m) => m.key)
                      .filter((key) => studyTypesPresent.has(key)),
                    ...[...studyTypesPresent].filter(
                      (key) => !modalities.some((m) => m.key === key),
                    ),
                  ];

                  return (
                  <Fragment key={p.uhid}>
                  <TableRow
                    data-testid={`patient-row-${p.uhid}`}
                    className={`cursor-pointer hover:bg-black/[0.02] ${
                      studyTypesList.length > 0
                        ? "patient-row-continues [&>td]:!pb-0"
                        : "patient-block-end"
                    }`}
                    onClick={() => navigate(`/patients/${p.uhid}`)}
                  >
                    <TableCell className="min-w-[240px]">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={`w-8 h-8 rounded-full flex-shrink-0 grid place-items-center text-[10px] font-bold text-white ${
                            p.status === "completed" ? "bg-[var(--xl-primary)]" : avatarTone(p.uhid)
                          }`}
                          aria-hidden
                        >
                          {patientInitials(p.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--xl-text)] truncate">{p.name || "—"}</div>
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5 min-w-0 text-xs text-[var(--xl-mut)]">
                            <span className="truncate">{formatPatientDisplayUhid(p)}</span>
                            {pendingDeletion && (
                              <Badge
                                className="text-[10px] font-normal shrink-0 border-transparent bg-amber-100 text-amber-800 rounded-md px-1.5 py-0 h-auto leading-normal"
                                data-testid={`patient-pending-deletion-${p.uhid}`}
                              >
                                Pending deletion
                              </Badge>
                            )}
                            {(p.tags ?? []).map((tag) => (
                              <Badge
                                key={tag}
                                className="text-xs font-normal shrink-0 badge-mri border-transparent rounded-md px-1.5 py-0 h-auto leading-normal"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="w-28 text-[var(--xl-mut)]">
                      {[p.age, p.gender].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell>{p.referring_doctor || "-"}</TableCell>
                    <TableCell>
                      {hospitalName && hospitalDisplay !== hospitalName ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">{hospitalDisplay}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top">{hospitalName}</TooltipContent>
                        </Tooltip>
                      ) : (
                        hospitalDisplay
                      )}
                    </TableCell>
                    <TableCell className="min-w-[10rem] max-w-[16rem] text-[var(--xl-mut)]">
                      {anatomyType1Label ? (
                        <div className="leading-snug">
                          <div>{anatomyType1Label}</div>
                          {anatomyType2Label ? (
                            <div>
                              {anatomyType2Label}
                              {anatomyType3Label ? ` · ${anatomyType3Label}` : ""}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {showActions && (
                      <TableCell className="w-60 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canManagePatients && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={getPatientStatusValue(p.status)}
                                onValueChange={(value) => {
                                  if (value === "pending") return;
                                  openMarkCompleteDialog(p);
                                }}
                                disabled={updatingStatusUhid === p.uhid || p.status === "completed"}
                              >
                                <SelectTrigger
                                  className={`h-7 w-[6.5rem] text-xs ${
                                    p.status === "completed"
                                      ? "border-[var(--xl-success)] text-[var(--xl-success)] bg-[var(--xl-success-bg)] disabled:opacity-100 disabled:cursor-default"
                                      : ""
                                  }`}
                                  data-testid={`patient-status-${p.uhid}`}
                                  aria-label={`Status for ${p.name ?? p.uhid}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent onClick={(e) => e.stopPropagation()}>
                                  <SelectItem value="pending">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {canManagePatients && (
                            <button
                              type="button"
                              data-testid={`edit-patient-${p.uhid}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditPatient(p);
                              }}
                              className="w-8 h-8 hover:bg-secondary rounded-full inline-flex items-center justify-center border border-[var(--xl-line)]"
                              title="Edit patient"
                              aria-label={`Edit ${p.name || p.uhid}`}
                            >
                              <Pencil className="w-3.5 h-3.5 text-[var(--xl-text)]" />
                            </button>
                          )}
                          {canManagePatients && !pendingDeletion && (
                            <button
                              type="button"
                              data-testid={`delete-patient-${p.uhid}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletePatient(p);
                              }}
                              className="w-8 h-8 hover:bg-red-50 rounded-full inline-flex items-center justify-center border border-[var(--xl-line)]"
                              title="Delete patient"
                              aria-label={`Delete ${p.name || p.uhid}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          )}
                          {canShare && (
                            <button
                              type="button"
                              data-testid={`share-patient-${p.uhid}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSharePatient(p);
                              }}
                              className="w-8 h-8 hover:bg-secondary rounded-full inline-flex items-center justify-center border border-[var(--xl-line)]"
                              title="Share patient"
                              aria-label={`Share ${p.name || p.uhid}`}
                            >
                              <Share2 className="w-3.5 h-3.5 text-[var(--xl-text)]" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {studyTypesList.length > 0 ? (
                    <TableRow
                      data-testid={`patient-studies-row-${p.uhid}`}
                      className="patient-block-end cursor-pointer hover:bg-black/[0.02] [&>td]:!pt-2 [&>td]:!pb-2.5"
                      onClick={() => navigate(`/patients/${p.uhid}`)}
                    >
                      <TableCell colSpan={colSpan}>
                        <div
                          className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pl-[2.625rem]"
                          aria-label="Study types"
                        >
                          {studyTypesList.map((type, i) => {
                            const label = getModality(type)?.label ?? type.replace(/_/g, " ");
                            const count = studyTypeCounts[type];
                            return (
                              <span
                                key={`${p.uhid}-${type}-${i}`}
                                className="inline-flex items-center gap-1.5 text-xs text-black"
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: getModalityChartColor(type) }}
                                  aria-hidden
                                />
                                {label}
                                {Number.isFinite(count) ? (
                                  <span>({count})</span>
                                ) : null}
                              </span>
                            );
                          })}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-[var(--xl-line)] px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-end gap-3 bg-white">
          <div className="flex items-center gap-3 flex-shrink-0">
            <p className="text-xs sm:text-sm text-[var(--xl-mut)] whitespace-nowrap">
              Page {page} of {totalPages} · {total} {total === 1 ? "patient" : "patients"}
            </p>
            <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={loading || isFirstPage}
              aria-label="Go to previous patients page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={loading || isLastPage}
              aria-label="Go to next patients page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            </div>
          </div>
        </div>
      </Card>

      <SharePatientDialog
        open={!!sharePatient}
        onOpenChange={(open) => { if (!open) setSharePatient(null); }}
        patient={sharePatient}
      />

      <EditPatientDialog
        open={!!editPatient}
        onOpenChange={(open) => { if (!open) setEditPatient(null); }}
        patient={editPatient}
        uhid={editPatient?.uhid}
        onSaved={() => fetchPatients(search, page)}
      />

      <Dialog open={!!completeDialogPatient} onOpenChange={(open) => { if (!open) setCompleteDialogPatient(null); }}>
        <DialogContent className="max-w-md" data-testid="mark-complete-dialog">
          <DialogHeader>
            <DialogTitle>Mark Case Complete</DialogTitle>
            <DialogDescription>
              Select company and size before marking complete. Valve type is required for Medtronic.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Company *</Label>
              <Select
                value={selectedCompany || undefined}
                onValueChange={(value) => setCompletionForm(formFromCompany(value))}
              >
                <SelectTrigger data-testid="completion-company">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isMedtronicCompany && (
              <div className="space-y-1.5">
                <Label className="text-xs">Valve Type *</Label>
                <Select
                  value={completionForm.valve_type || undefined}
                  onValueChange={(value) => setCompletionForm((prev) => ({
                    ...prev,
                    valve_type: value,
                    valve_size: "",
                  }))}
                >
                  <SelectTrigger data-testid="completion-valve-or-company">
                    <SelectValue placeholder="Select valve type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDTRONIC_VALVE_TYPES.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Size *</Label>
              {isNaOthers ? (
                <Input
                  data-testid="completion-size"
                  value={completionForm.valve_size}
                  onChange={(e) => setCompletionForm((prev) => ({ ...prev, valve_size: e.target.value }))}
                  placeholder="Enter size"
                  className="h-9"
                />
              ) : (
                <Select
                  value={completionForm.valve_size || undefined}
                  onValueChange={(value) => setCompletionForm((prev) => ({ ...prev, valve_size: value }))}
                  disabled={!completionForm.valve_type || !sizeOptions?.length}
                >
                  <SelectTrigger data-testid="completion-size">
                    <SelectValue placeholder={sizeOptions?.length ? "Select size" : "Select company first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {sizeOptions?.map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogPatient(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMarkComplete}
              disabled={updatingStatusUhid === completeDialogPatient?.uhid}
              data-testid="completion-submit"
            >
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePatient} onOpenChange={(open) => { if (!open && !deleting) setDeletePatient(null); }}>
        <AlertDialogContent data-testid="delete-patient-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete patient?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-medium text-foreground">
                {deletePatient?.name || formatPatientDisplayUhid(deletePatient)}
              </span>
              {" "}({formatPatientDisplayUhid(deletePatient)}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-patient-confirm"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </Collapsible>
    </TooltipProvider>
  );
}
